import store from 'js/lib/coursera.store';

/**
 * User-defined request headers configured via the dev toolbar's "Test header" panel and applied
 * to GraphQL/REST requests made from the browser. Stored in localStorage (via `js/lib/coursera.store`,
 * which degrades gracefully when storage is disabled) under the `_chkvp` key (custom header key-value pairs).
 */
const CUSTOM_HEADERS_STORAGE_KEY = '_chkvp';

/** Dispatched whenever `setCustomHeaders` is called, so open toolbar UI (e.g. the status tag) can refresh. */
export const CUSTOM_HEADERS_CHANGED_EVENT = 'coursera-toolbar-custom-headers-changed' as const;

export interface CustomHeaderEntry {
  id: string;
  name: string;
  value: string;
  enabled: boolean;
}

function isCustomHeaderEntry(value: unknown): value is CustomHeaderEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.name === 'string' &&
    typeof entry.value === 'string' &&
    typeof entry.enabled === 'boolean'
  );
}

function generateHeaderEntryId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Builds a new, blank entry (not persisted until passed to `setCustomHeaders`).
 */
export function createCustomHeaderEntry(): CustomHeaderEntry {
  return { id: generateHeaderEntryId(), name: '', value: 'test', enabled: true };
}

/**
 * Reads all configured custom headers, on or off.
 */
export function getCustomHeaders(): CustomHeaderEntry[] {
  const stored: unknown = store.get(CUSTOM_HEADERS_STORAGE_KEY);
  return Array.isArray(stored) ? stored.filter(isCustomHeaderEntry) : [];
}

/**
 * Persists the full list of custom headers, replacing whatever was there before.
 */
export function setCustomHeaders(headers: CustomHeaderEntry[]): void {
  store.set(CUSTOM_HEADERS_STORAGE_KEY, headers);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CUSTOM_HEADERS_CHANGED_EVENT));
  }
}

/**
 * Returns only the enabled, named headers as a plain header map, ready to merge into a request.
 * Entries with a blank name are ignored even if enabled.
 */
export function getActiveCustomHeaders(): Record<string, string> {
  return getCustomHeaders().reduce<Record<string, string>>((headers, entry) => {
    const name = entry.name.trim();
    if (entry.enabled && name.length > 0) {
      return { ...headers, [name]: entry.value };
    }
    return headers;
  }, {});
}
